import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, cardProduct, inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { CATEGORIES } from "@/data/products";

export default function SearchOverlay() {
  const { searchOpen, setSearchOpen, addToCart, requireAuth, catalog, pincodeInfo } = useStore();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  const brandMap = useMemo(
    () => Object.fromEntries((catalog.brands || []).map((b) => [b.id, b.name])),
    [catalog.brands]
  );

  useEffect(() => {
    if (searchOpen) { window.__lenis?.stop(); setQuery(""); setResults([]); }
    else window.__lenis?.start();
  }, [searchOpen]);

  useEffect(() => {
    const fn = (e) => e.key === "Escape" && setSearchOpen(false);
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [setSearchOpen]);

  useEffect(() => {
    const q = query.trim();
    if (!q) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get("/ecom/catalog/products", { params: { q, page_size: 8, ...(pincodeInfo?.store_id && { store_id: pincodeInfo.store_id }) } })
        .then((r) => setResults(r.data?.items || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const open = (p) => {
    setSearchOpen(false);
    navigate(`/product/${p.slug || p.id}`);
  };

  return (
    <AnimatePresence>
      {searchOpen && (
        <motion.div data-testid="search-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[85] bg-forest/95 backdrop-blur-xl">
          <div className="mx-auto max-w-3xl px-4 pt-24 sm:pt-32">
            <div className="flex items-center gap-4 border-b-2 border-gold/40 pb-4">
              <Search className="h-6 w-6 shrink-0 text-gold" />
              <input
                data-testid="search-input"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search brands, rituals, products..."
                className="w-full bg-transparent font-display text-2xl text-cream placeholder:text-fern focus:outline-none sm:text-4xl"
              />
              <button data-testid="search-close-button" onClick={() => setSearchOpen(false)} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-champagne/40 text-cream hover:border-gold hover:text-gold" aria-label="Close search">
                <X className="h-4 w-4" />
              </button>
            </div>

            {!query.trim() && (
              <div className="mt-8" data-testid="search-suggestions">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-fern">Popular rituals</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {CATEGORIES.map((c) => (
                    <button key={c.slug} data-testid={`search-chip-${c.slug}`} onClick={() => setQuery(c.name)} className="rounded-full border border-champagne/50 px-4 py-2 text-xs text-sage transition-colors hover:border-gold hover:text-gold">
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {query.trim() && (
              <div className="mt-6 max-h-[55vh] overflow-y-auto no-scrollbar" data-testid="search-results">
                {searching ? (
                  <p className="py-10 text-center font-mono text-xs uppercase tracking-[0.25em] text-fern">Searching the shelf…</p>
                ) : results.length === 0 ? (
                  <p className="py-10 text-center font-display text-xl text-fern" data-testid="search-no-results">
                    Nothing in the sanctuary matches “{query}” yet
                  </p>
                ) : (
                  <ul className="divide-y divide-champagne/30">
                    {results.map((raw) => {
                      const p = cardProduct(raw, brandMap[raw.brand_id]);
                      return (
                        <li key={p.id} data-testid={`search-result-${p.id}`} className="flex items-center gap-4 py-4">
                          <button onClick={() => open(p)} className="flex flex-1 items-center gap-4 text-left">
                            <div className="h-14 w-12 shrink-0 overflow-hidden rounded-lg border border-champagne/40">
                              <img src={p.img} alt={p.name} className="h-full w-full object-cover" />
                            </div>
                            <div className="flex-1">
                              {p.brand && <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-gold">{p.brand}</p>}
                              <p className="line-clamp-1 font-display text-base text-cream">{p.name}</p>
                            </div>
                            <span className="text-sm font-bold text-cream">{inr(p.price)}</span>
                          </button>
                          <button
                            data-testid={`search-add-${p.id}`}
                            onClick={() => requireAuth(() => addToCart(p.id, 1))}
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-cream text-forest hover:bg-gold"
                            aria-label="Add to bag"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
